import Badge from '../ui/Badge';
import { ShieldCheckIcon, CalendarIcon, ClockIcon } from '../ui/icons';

// Static, representative previews for the landing-page carousel — the
// Investment Plans endpoint requires authentication (GET
// /investor/investment-plans), so pre-login this reuses the app's real
// card/badge/pill visual language with illustrative numbers instead of
// faking a live data call, per the "show the real product, don't fake
// live data pre-login" instruction. Clearly labeled as illustrative on
// the slide itself so nothing here reads as an authoritative live figure.

function PlansSlideVisual() {
  const plans = [
    { name: 'Starter 3mo', roi: '8%', range: '₦50,000+' },
    { name: 'Growth 12mo', roi: '18%', range: '₦250,000+' },
    { name: 'Fixed 6mo', roi: '12%', range: '₦100,000+' },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {plans.map((p) => (
        <div key={p.name} className="glass-card p-4 text-left">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-foreground">{p.name}</p>
            <Badge tone="success">{p.roi} ROI</Badge>
          </div>
          <p className="text-xs text-foreground-subtle mt-2">From {p.range}</p>
        </div>
      ))}
    </div>
  );
}

function DashboardSlideVisual() {
  const stats = [
    { label: 'Total invested', value: '₦850,000' },
    { label: 'Active investments', value: '3' },
    { label: 'Interest earned', value: '₦64,200', accent: true },
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {stats.map((s) => (
        <div key={s.label} className="glass-card p-4">
          <p className="text-xs text-foreground-subtle">{s.label}</p>
          <p className={`text-lg sm:text-xl font-bold mt-1.5 tabular-nums ${s.accent ? 'text-accent' : 'text-foreground'}`}>
            {s.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function SecuritySlideVisual() {
  return (
    <div className="glass-card p-6 flex items-center gap-4">
      <div className="h-14 w-14 rounded-2xl bg-accent-soft text-accent flex items-center justify-center shrink-0">
        <ShieldCheckIcon className="h-7 w-7" />
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground">KYC verified</p>
        <p className="text-xs text-foreground-subtle mt-1">
          Identity checks before any investor gets portfolio access — reviewed by an admin, not automated guesswork.
        </p>
      </div>
    </div>
  );
}

function PayoutScheduleSlideVisual() {
  const upcoming = [
    { label: 'This month', sub: 'Interest payout', done: true },
    { label: 'Next month', sub: 'Interest payout', done: false },
    { label: 'In 2 months', sub: 'Interest payout', done: false },
  ];
  return (
    <div className="glass-card p-5 text-left max-w-sm mx-auto">
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-sm font-semibold text-foreground">Payout schedule</p>
        <Badge tone="success">Monthly</Badge>
      </div>
      <p className="text-xs text-foreground-subtle flex items-center gap-1.5 mb-4">
        <ClockIcon className="h-3.5 w-3.5" /> Next payout due in 12 days
      </p>
      <ol className="space-y-3">
        {upcoming.map((u, i) => (
          <li key={u.label} className="flex items-center gap-3">
            <span
              className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${
                u.done ? 'bg-accent text-white' : 'bg-background-elevated text-foreground-subtle border border-border'
              }`}
            >
              <CalendarIcon className="h-3 w-3" />
            </span>
            <span className="text-xs text-foreground-muted">
              <span className="text-foreground font-medium">{u.label}</span> · {u.sub}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export const CAROUSEL_SLIDES = [
  {
    id: 'plans',
    eyebrow: 'Investment plans',
    title: 'Browse curated investment plans',
    body: 'Fixed-return plans with a clear ROI, duration, and minimum — pick what fits, an admin sets it up for you.',
    visual: <PlansSlideVisual />,
    note: 'Illustrative plan examples — see live rates after signing in.',
  },
  {
    id: 'dashboard',
    eyebrow: 'Your portfolio',
    title: 'Track your portfolio in real time',
    body: 'Total invested, active investments, and interest earned — always current, one glance from login.',
    visual: <DashboardSlideVisual />,
    note: 'Illustrative dashboard preview.',
  },
  {
    id: 'security',
    eyebrow: 'Trust & safety',
    title: 'Verified and secure',
    body: "No investor sees live portfolio data until their identity is verified and approved by an admin.",
    visual: <SecuritySlideVisual />,
  },
  {
    id: 'payout-schedule',
    eyebrow: 'Automatic payouts',
    title: 'Automatic payout scheduling',
    body: 'Interest lands on a fixed cadence — monthly or quarterly — with the next due date always visible, no chasing required.',
    visual: <PayoutScheduleSlideVisual />,
    note: 'Illustrative schedule preview.',
  },
];

export default CAROUSEL_SLIDES;
